import { fmtVal, GREEN } from './GoalViz.jsx';
import { shortDate, multipleName } from '../../lib/goalOver.mjs';

// The goal detail's time + achievement blocks (#156). The card only carries a quiet
// "27 days left · Hit 22 Oct" line; the full picture lives here:
//   • Time window — the goal's OWN start → by date: days to go, day N of M, and a
//     strip with today (dot) and the day the target was hit (diamond).
//   • Achievement record — the date the target was hit, each whole multiple after it
//     (doubled, tripled…), the value on the day, and the gain since. Fixed once
//     written; raising the target starts it over.

export function TimeWindow({ p }) {
  const w = p.window;
  if (!w) return null;
  const hitAt = p.achievement?.hitAt;
  const pos = (iso) => Math.max(0, Math.min(100, (daysFromStart(w, iso) / Math.max(1, w.totalDays)) * 100));
  const elapsed = (w.day / Math.max(1, w.totalDays)) * 100;
  return (
    <div style={block}>
      <div style={blockHead}>Time window</div>
      <div style={{ fontSize: 15, fontWeight: 800 }}>
        {w.closed ? `Closed ${shortDate(w.end)}` : w.daysLeft === 0 ? 'Last day' : `${w.daysLeft} day${w.daysLeft === 1 ? '' : 's'} to go`}
        <span style={{ fontWeight: 600, color: 'var(--muted)' }}> · {w.closed ? `${w.totalDays} days` : `day ${w.day} of ${w.totalDays}`}</span>
      </div>
      <div role="img" aria-label={`Window ${w.start} to ${w.end}${hitAt ? `, target hit ${shortDate(hitAt)}` : ''}`}
        style={{ position: 'relative', height: 6, borderRadius: 980, background: 'rgba(128,128,128,0.16)', margin: '12px 0 6px' }}>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${elapsed}%`, borderRadius: 980, background: 'var(--muted)', opacity: 0.55 }} />
        {hitAt && <i title={`Target hit ${shortDate(hitAt)}`} style={{ ...marker, left: `${pos(hitAt)}%`, width: 12, height: 12, marginLeft: -6, marginTop: -6, transform: 'rotate(45deg)', borderRadius: 2, background: GREEN }} />}
        {!w.closed && <i title="Today" style={{ ...marker, left: `${elapsed}%`, width: 12, height: 12, marginLeft: -6, marginTop: -6, borderRadius: '50%', background: 'var(--text)' }} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)' }}>
        <span>{shortDate(w.start)}</span><span>{shortDate(w.end)}</span>
      </div>
    </div>
  );
}

export function AchievementRecord({ goal, p }) {
  const a = p.achievement;
  if (!a || !a.marks?.length) return null;
  const w = p.window;
  const dayOf = (iso) => (w ? daysFromStart(w, iso) : null);
  const dayIn = (iso) => { const n = dayOf(iso); return n != null && n >= 0 && n <= w.totalDays ? ` · day ${n}` : ''; };
  const first = a.marks.find((m) => m.multiple === 1);
  const early = first && w && dayOf(first.at) != null ? w.totalDays - dayOf(first.at) : null;
  const since = first && p.value != null && first.value != null ? Number(p.value) - Number(first.value) : null;
  const nextK = a.marks[a.marks.length - 1].multiple + 1;
  return (
    <div style={block}>
      <div style={blockHead}>Achievement record</div>
      <dl style={kv}>
        {a.marks.map((m) => (
          <Row key={m.multiple} label={multipleName(m.multiple)} good
            value={`${shortDate(m.at)}${dayIn(m.at)}${m.estimated ? ' (first seen)' : ''}`} />
        ))}
        {first && early > 0 && <Row label="Ahead of deadline" value={`${early} day${early === 1 ? '' : 's'} early`} />}
        {first && first.value != null && <Row label="Value on the day" value={fmtVal(Math.round(first.value), goal.unit)} />}
        {since != null && since > 0 && <Row label="Since then" good value={`+${fmtVal(Math.round(since), goal.unit)}${first.value ? ` (+${Math.round((since / first.value) * 100)}%)` : ''}`} />}
        {nextK <= 3 && p.pct >= 100 && <Row label={multipleName(nextK)} value="Not yet" />}
      </dl>
      {a.marks.some((m) => m.estimated) && (
        <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45 }}>"First seen" is the day Pulse first saw the goal past that line. Link a "last time" curve to date it exactly.</div>
      )}
    </div>
  );
}

// Shown to people who can edit the goal at 5× or more: a target that low is usually a typo.
export function TargetCheck({ goal, p, canManage }) {
  if (!canManage || (goal.direction || 'at_least') !== 'at_least' || !(p.pct >= 500)) return null;
  return (
    <div style={{ margin: '6px 0 2px', fontSize: 11.5, color: '#b45309', lineHeight: 1.45 }}>
      This goal is over 5× its target. If the target was set too low, edit the goal to raise it. The achievement record starts over when the target changes.
    </div>
  );
}

// Whole calendar days from the window's start to a timestamp (local dates).
function daysFromStart(w, iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - new Date(`${w.start}T00:00:00`).getTime()) / 86400000);
}

function Row({ label, value, good = false }) {
  return (
    <>
      <dt style={{ color: 'var(--muted)' }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: 650, textAlign: 'right', color: good ? GREEN : 'var(--text)' }}>{value}</dd>
    </>
  );
}

const block = { border: '1px solid var(--hairline)', borderRadius: 12, padding: '12px 14px', margin: '10px 0 4px', display: 'flex', flexDirection: 'column', gap: 6 };
const blockHead = { fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' };
const marker = { position: 'absolute', top: '50%', border: '2px solid var(--card)', boxSizing: 'border-box' };
const kv = { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 13, margin: 0, fontVariantNumeric: 'tabular-nums' };
